import { formatUserFacingError } from '@/lib/errors'
import { useState, useEffect, useCallback } from 'react'
import type { ApplicationDocument } from '@/types'
import { adminApplicationsService } from '../services/admin.applications.service'
import type { ApplicationFormValues } from '../types'

interface UseAdminApplicationsResult {
  applications: Array<ApplicationDocument & { id: string }>
  loading: boolean
  error: string | null
  refresh: () => void
  createApplication: (data: ApplicationFormValues) => Promise<string>
  updateApplication: (id: string, data: Partial<ApplicationFormValues>) => Promise<void>
  updateStatus: (id: string, status: ApplicationDocument['status']) => Promise<void>
  updatePublished: (id: string, isPublished: boolean) => Promise<void>
  deleteApplication: (id: string) => Promise<void>
}

export function useAdminApplications(): UseAdminApplicationsResult {
  const [applications, setApplications] = useState<Array<ApplicationDocument & { id: string }>>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)

  const refresh = useCallback(() => setTick((t) => t + 1), [])

  useEffect(() => {
    setLoading(true)
    setError(null)
    adminApplicationsService
      .getAll()
      .then(setApplications)
      .catch((err) => setError(formatUserFacingError(err, 'Failed to load applications')))
      .finally(() => setLoading(false))
  }, [tick])

  const createApplication = useCallback(async (data: ApplicationFormValues) => {
    const id = await adminApplicationsService.create(data)
    refresh()
    return id
  }, [refresh])

  const updateApplication = useCallback(async (id: string, data: Partial<ApplicationFormValues>) => {
    await adminApplicationsService.update(id, data)
    refresh()
  }, [refresh])

  const updateStatus = useCallback(async (id: string, status: ApplicationDocument['status']) => {
    await adminApplicationsService.updateStatus(id, status)
    refresh()
  }, [refresh])

  const updatePublished = useCallback(async (id: string, isPublished: boolean) => {
    await adminApplicationsService.updatePublished(id, isPublished)
    refresh()
  }, [refresh])

  const deleteApplication = useCallback(async (id: string) => {
    await adminApplicationsService.delete(id)
    refresh()
  }, [refresh])

  return {
    applications,
    loading,
    error,
    refresh,
    createApplication,
    updateApplication,
    updateStatus,
    updatePublished,
    deleteApplication,
  }
}
